import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, LogOut, Check, Edit2, X } from 'lucide-react';
import { profileAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetup = searchParams.get('setup') === 'true';
  const { user, logout, updateUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [editingName, setEditingName] = useState(isSetup);
  const [editingUPI, setEditingUPI] = useState(isSetup && !user?.upi_id);
  const [name, setName] = useState(user?.name || '');
  const [upiId, setUpiId] = useState(user?.upi_id || '');
  
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    setName(user?.name || '');
    setUpiId(user?.upi_id || '');
  }, [user]);

  const handleSaveName = async () => {
    if (!name.trim() || name.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const response = await profileAPI.update({ name: name.trim() });
      updateUser(response.data);
      setEditingName(false);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save';
      setError(typeof message === 'string' ? message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUPI = async () => {
    if (!upiId.trim()) {
      setError('Please enter a UPI ID');
      return;
    }
    
    // Basic UPI validation
    const upiPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z]{3,}$/;
    if (!upiPattern.test(upiId)) {
      setError('Invalid UPI ID format. Example: yourname@upi');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const response = await profileAPI.update({ upi_id: upiId.trim() });
      updateUser(response.data);
      setEditingUPI(false);
    } catch (err) {
      const message = err.response?.data?.detail;
      if (Array.isArray(message)) {
        setError(message[0]?.msg || 'Invalid UPI ID format');
      } else {
        setError(typeof message === 'string' ? message : 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    return `+91 ${phone.slice(0, 5)} ${phone.slice(5)}`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate('/home')}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">
          {isSetup ? 'Complete Your Profile' : 'Profile'}
        </h1>
      </header>

      <main className="flex-1 p-6 space-y-6">
        {/* Avatar & Phone */}
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-black text-white flex items-center justify-center font-bold text-3xl mx-auto mb-4">
            {getInitials(user?.name)}
          </div>
          <p className="text-lg font-medium text-neutral-600">
            {formatPhone(user?.phone_number)}
          </p>
          <p className="text-xs text-neutral-400">(cannot be changed)</p>
        </div>

        {/* Name Field */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Name
          </label>
          {editingName ? (
            <div className="space-y-2">
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                placeholder="Your name"
                className="input-base"
                autoFocus
                data-testid="name-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveName}
                  disabled={saving}
                  className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
                  data-testid="save-name-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save
                </button>
                {!isSetup && (
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setName(user?.name || '');
                      setError('');
                    }}
                    className="btn-secondary px-4 py-3"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="card-base flex items-center justify-between">
              <p className="font-bold text-lg">{user?.name || 'Not set'}</p>
              <button
                onClick={() => setEditingName(true)}
                className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
                data-testid="edit-name-button"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* UPI ID Field */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            UPI ID
          </label>
          {editingUPI ? (
            <div className="space-y-2">
              <input
                type="text"
                value={upiId}
                onChange={(e) => {
                  setUpiId(e.target.value.toLowerCase());
                  setError('');
                }}
                placeholder="yourname@upi"
                className="input-base font-mono"
                data-testid="upi-input"
              />
              <p className="text-xs text-neutral-500">
                Used for receiving payments
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveUPI}
                  disabled={saving}
                  className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
                  data-testid="save-upi-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save
                </button>
                {!isSetup && user?.upi_id && (
                  <button
                    onClick={() => {
                      setEditingUPI(false);
                      setUpiId(user?.upi_id || '');
                      setError('');
                    }}
                    className="btn-secondary px-4 py-3"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ) : user?.upi_id ? (
            <div className="card-base flex items-center justify-between">
              <div>
                <p className="font-bold text-lg font-mono">{user.upi_id}</p>
                <p className="text-xs text-neutral-500">Used for receiving payments</p>
              </div>
              <button
                onClick={() => setEditingUPI(true)}
                className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
                data-testid="edit-upi-button"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditingUPI(true)}
              className="card-base w-full text-left border-dashed border-yellow-500 bg-yellow-50"
              data-testid="add-upi-button"
            >
              <p className="font-bold text-yellow-700">Add UPI ID</p>
              <p className="text-sm text-yellow-600">Required to receive payments</p>
            </button>
          )}
        </div>

        {error && (
          <p className="text-error text-sm font-medium text-center" data-testid="error-message">
            {error}
          </p>
        )}

        {/* Setup completion hint */}
        {isSetup && (!user?.name || !user?.upi_id) && (
          <div className="card-base bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-700">
              Complete your profile to start splitting bills!
            </p>
          </div>
        )}

        {/* Continue button for setup flow */}
        {isSetup && user?.name && (
          <button
            onClick={() => navigate('/home')}
            className="btn-primary w-full"
            data-testid="continue-button"
          >
            {user?.upi_id ? 'Continue to Home' : 'Skip for Now'}
          </button>
        )}

        {/* Logout */}
        {!isSetup && (
          <div className="pt-6 border-t border-neutral-200">
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full py-4 text-error font-bold flex items-center justify-center gap-2 hover:bg-red-50 rounded-xl transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        )}

        {/* Version */}
        <p className="text-center text-xs text-neutral-400 pt-4">
          App version 1.0.0
        </p>
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="card-base max-w-sm w-full text-center">
            <h2 className="text-xl font-heading font-bold mb-2">
              Log out of SplitPe?
            </h2>
            <p className="text-neutral-600 mb-6">
              You'll need to verify your phone number again
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="btn-secondary flex-1 py-3"
                data-testid="cancel-logout-button"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-3 bg-error text-white font-bold rounded-full border-2 border-black shadow-hard active:translate-x-[2px] active:translate-y-[2px] active:shadow-hard-hover transition-all"
                data-testid="confirm-logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
